import { get, isEmpty, isNil, startCase, includes, size, map } from 'lodash';
import moment from 'moment';
import React from 'react';
import { ChevronRight } from '@mui/icons-material';
import { ChargerTip } from '../../../../../common/components/Form/legacy/RenderChargerDropdownAutocomplete';

import HintableText from '../../HintableText';
import {
  EVENT_LIST_LABELS,
  EVENT_TYPE_AUTHORIZATION_REQUEST,
  EVENT_TYPE_FINISH_APPLYING_LIMIT,
  EVENT_TYPE_FINISH_APPLYING_LIMIT_W_OWNER,
  EVENT_TYPE_FINISH_PLAZA_NODE_CHARGER_LIMITS_UPDATE,
  EVENT_TYPE_FINISH_PLAZA_NODE_HARD_CONSTRAINT_LIMITS_UPDATE,
  EVENT_TYPE_FINISH_PLAZA_NODE_LOAD_LIMITS_UPDATE,
  EVENT_TYPE_FINISH_PLAZA_NODE_POWER_LIMITS_UPDATE,
  EVENT_TYPE_FINISH_PLAZA_NODE_DYNAMIC_LIMITS_UPDATE,
  EVENT_TYPE_MEASUREMENT_POINT_UPDATE,
  EVENT_TYPE_ONLINE_NO_SESSION,
  EVENT_TYPE_OVER_PROTECTION_TRIGGER,
  EVENT_TYPE_ROGUE_CHARGERS_DETECTED,
  EVENT_TYPE_UNDER_CONSUMPTION_TRIGGER,
  EVENT_TYPE_FAULT,
} from '../helper';

export const formatTsTitle = (ts) => {
  const momentInst = moment.unix(ts).utc();

  return `${momentInst.format('L')} ${momentInst.format('LTS')}`;
};

export const formatTsMillisecondsTitle = (ts) => {
  const momentInst = moment(ts).utc();

  return `${momentInst.format('L')} ${momentInst.format('LTS')}`;
};

const priorityList = [
  'current_requested',
  'current_discharging_requested',
  'energy_requested',
  'power_discharging_requested',
  'number_of_phases',
];

const sortValues = (prev, next) => {
  const prevIndex = priorityList.indexOf(prev);
  const nextIndex = priorityList.indexOf(next);

  if (prevIndex === nextIndex) {
    return 0;
  }

  return prevIndex > nextIndex ? 1 : -1;
};

const EventLabel = ({ plazaEventType, values }) => {
  let label = get(EVENT_LIST_LABELS, plazaEventType, '');

  const noValues =
    [
      EVENT_TYPE_FINISH_APPLYING_LIMIT, // Limit Applied
      EVENT_TYPE_AUTHORIZATION_REQUEST, // Authorization Request Issued
    ].includes(plazaEventType) && isEmpty(values);

  if (noValues) {
    label = get(EVENT_LIST_LABELS, EVENT_TYPE_ONLINE_NO_SESSION, '');
  }

  return label;
};

const renderValue = (value, unit = '', multiply = 1, accuracy = 2) => {
  if (isNil(value) || value === '') {
    return null;
  }

  return `${(Number.parseFloat(value) * multiply).toFixed(accuracy)}${unit}`;
};

const mappedPhases = {
  1: 'Ph-A',
  2: 'Ph-B',
  3: 'Ph-C',
};

const mapEnergyConsumption = (val, showAllowed = true) => {
  const nameOrId = val.charger_name || val.charger_uuid;
  const phase = mappedPhases[val.phase] || '';
  const load = renderValue(val.current_load, 'A');
  const limit = renderValue(val.limit, 'A');

  if (showAllowed) {
    return `${nameOrId} ${phase} consumes ${load} of ${limit} allowed`;
  }

  return `${nameOrId} ${phase} consumes ${load}`;
};

const valuesBlock = (label, value, key) => (
  <div className="values" key={key}>
    <div className="bold">{label}:</div>
    <div>
      <HintableText text={value} />
    </div>
  </div>
);

const mapLoadByPhases = (values, processedEventName) => {
  const parsedValue = JSON.parse(values) || {};
  const keys = Object.keys(parsedValue) || [];
  return isEmpty(keys)
    ? valuesBlock(processedEventName, 'N/A', processedEventName)
    : map(keys, (key, index) => {
        const phase = mappedPhases[key] || '';
        const load = renderValue(parsedValue[key], 'A');
        return valuesBlock(`${processedEventName} ${phase}`, load, index);
      });
};

const getMappedReasonValue = (value) => {
  switch (value) {
    case 'SUSPENDED_EV':
      return 'Suspended EV';
    case 'SUSPENDED_EVSE':
      return 'Suspended EVSE';
    default:
      return startCase(value.toLowerCase().replaceAll('_', ' '));
  }
};

const excludedValues = [
  'breaker_limit',
  'breaker_name',
  'owner',
  'old_max_power',
  'old_max_load',
  'old_min_load_limit',
  'old_offline_load_limit',
  'node_type',
];

function LogView(props) {
  const {
    data: { uuid = null, ts = null, chargerName = '', values = {}, nodeName },
    isNew = false,
    opened = false,
    toggleOpen,
  } = props;

  let {
    data: { plazaEventType = '' },
  } = props;

  if (plazaEventType === EVENT_TYPE_FINISH_APPLYING_LIMIT && includes(['OVERLOAD_PROTECTION', 'UNDER_CONSUMPTION'], values.owner)) {
    plazaEventType = EVENT_TYPE_FINISH_APPLYING_LIMIT_W_OWNER;
  }

  let title = chargerName;

  if (
    [
      EVENT_TYPE_FINISH_PLAZA_NODE_POWER_LIMITS_UPDATE,
      EVENT_TYPE_FINISH_PLAZA_NODE_LOAD_LIMITS_UPDATE,
      EVENT_TYPE_FINISH_PLAZA_NODE_DYNAMIC_LIMITS_UPDATE,
      EVENT_TYPE_FINISH_PLAZA_NODE_HARD_CONSTRAINT_LIMITS_UPDATE,
      EVENT_TYPE_FINISH_PLAZA_NODE_CHARGER_LIMITS_UPDATE,
      EVENT_TYPE_MEASUREMENT_POINT_UPDATE,
    ].includes(plazaEventType) &&
    nodeName
  ) {
    title = nodeName;
  }

  return (
    <div className={`log-item ${isNew ? 'new' : ''}`} key={uuid}>
      <div className="time-title">{ts ? formatTsMillisecondsTitle(ts) : ''} (UTC)</div>
      <div className="id-title bold">
        <ChargerTip text={title} maxTextSize={32} hintText={chargerName} />
      </div>
      <div className="id-title status">
        <div className={`circle ${plazaEventType || ''}`} />
        <EventLabel plazaEventType={plazaEventType} values={values} nodeName={nodeName} />
      </div>
      {size(Object.keys(values)) > 0 && (
        <ChevronRight className={`expand-icon ${opened ? 'opened' : ''}`} onClick={() => toggleOpen(uuid)} />
      )}

      <div className={`details ${opened ? 'opened' : 'closed'}`}>
        {includes([EVENT_TYPE_OVER_PROTECTION_TRIGGER, EVENT_TYPE_UNDER_CONSUMPTION_TRIGGER], plazaEventType) && (
          <div className="values">
            <div>
              {plazaEventType === EVENT_TYPE_OVER_PROTECTION_TRIGGER
                ? `Over protection triggered for ${values.breaker_name} (${renderValue(values.breaker_limit, 'A')})`
                : 'Under Utilization triggered'}
            </div>
          </div>
        )}

        {Object.keys(values)
          .filter((value) => !includes(excludedValues, value))
          .sort(sortValues)
          .map((eventName, i) => {
            let value;
            let processedEventName;

            switch (eventName) {
              case 'start_time':
                processedEventName = 'Start Time';
                value = values[eventName] ? formatTsTitle(values[eventName]) : '';
                break;
              case 'end_time':
                processedEventName = 'End Time';
                value = values[eventName] ? formatTsTitle(values[eventName]) : '';
                break;
              case 'departure_time':
                processedEventName = 'Departure Time';
                value = values[eventName] ? formatTsTitle(values[eventName]) : '';
                break;
              case 'energy_limit':
                processedEventName = 'Charging Power Limit';
                value = renderValue(values[eventName], 'kW', 0.001);
                break;
              case 'energy_requested':
                processedEventName = 'Requested Charging Power';
                value = renderValue(values[eventName], 'kW', 0.001);
                break;
              case 'current_limit':
              case 'confirmed_charging_load':
                processedEventName = 'Charging Load Limit';
                value = renderValue(values[eventName], 'A');
                break;
              case 'current_discharging_limit':
              case 'confirmed_discharging_load':
                processedEventName = 'Discharging Load Limit';
                value = renderValue(values[eventName], 'A');
                break;
              case 'power_discharging_requested':
                processedEventName = 'Requested Discharging Power';
                value = renderValue(values[eventName], 'kW', 0.001);
                break;
              case 'power_discharging_limit':
                processedEventName = 'Discharging Power Limit';
                value = renderValue(values[eventName], 'kW', 0.001);
                break;
              case 'current_requested':
                processedEventName = 'Requested Charging Load';
                value = renderValue(values[eventName], 'A');
                break;
              case 'current_discharging_requested':
                processedEventName = 'Requested Discharging Load';
                value = renderValue(values[eventName], 'A');
                break;
              case 'number_of_phases':
                processedEventName = 'No. of Phases during this Session';
                value = values[eventName] || 0;
                break;
              case 'session_type':
                processedEventName = 'Session Type';
                value = values[eventName] || '';
                break;
              case 'session_id':
                processedEventName = 'Session ID';
                value = values[eventName] || '';
                break;
              case 'session_info':
                processedEventName = 'Session Info';
                value = values[eventName] || '';
                break;
              case 'fault_info':
                processedEventName = 'Fault Info';
                value = values[eventName] || '';
                break;
              case 'power_autorization':
                processedEventName = 'Power Authorization';
                value = values[eventName] || '';
                break;
              case 'code':
                processedEventName = plazaEventType === EVENT_TYPE_FAULT ? 'Error Code' : 'Code';
                value = values[eventName] || '';
                break;
              case 'description':
                processedEventName = plazaEventType === EVENT_TYPE_FAULT ? 'Short Description' : 'Description';
                value = values[eventName] || '';
                break;
              case 'source':
                processedEventName = plazaEventType === EVENT_TYPE_FAULT ? 'Module Name' : 'Source';
                value = values[eventName] || '';
                break;
              case 'chargers_energy_consumption': {
                processedEventName = values[eventName].length
                  ? map(JSON.parse(values[eventName]), (value) =>
                      mapEnergyConsumption(value, plazaEventType !== EVENT_TYPE_ROGUE_CHARGERS_DETECTED),
                    )
                  : [];
                break;
              }
              case 'user_name':
                processedEventName = 'Created by';
                value = values[eventName] || '';
                break;
              case 'session_phases_updates': {
                processedEventName = values[eventName].length
                  ? map(JSON.parse(values[eventName]), (val) => {
                      const chargerName = val.charger_name || 'N/A';
                      const phases = val.number_of_phases || 'N/A';

                      return `${chargerName}: number of Phases has changed to ${phases} phase(s)`;
                    })
                  : [];
                break;
              }
              case 'max_power':
                processedEventName = 'Max Power';
                value = renderValue(values[eventName], 'kW', 0.001);
                break;
              case 'max_load':
                processedEventName = 'Max Load';
                value = renderValue(values[eventName], 'A');
                break;
              case 'min_load_limit':
                processedEventName = 'Min Load';
                value = renderValue(values[eventName], 'A');
                break;
              case 'offline_load_limit':
                processedEventName = 'Max Offline Load';
                value = renderValue(values[eventName], 'A');
                break;
              case 'safety_margin_percent':
                processedEventName = 'Safety Margin';
                value = renderValue(values[eventName], '%');
                break;
              case 'power':
                processedEventName = 'Present Power';
                value = renderValue(values[eventName], 'kW', 0.001);
                break;
              case 'load':
                processedEventName = 'Present Load';
                value = renderValue(values[eventName], 'A');
                break;
              case 'reason':
                processedEventName = 'Reason';
                value = getMappedReasonValue(values[eventName]);
                break;

              default:
                processedEventName = eventName;
                value = values[eventName];
            }
            if (eventName === 'chargers_energy_consumption' || eventName === 'session_phases_updates') {
              return map(processedEventName, (val) => (
                <div key={val} className="values with-disk">
                  <div>{val}</div>
                </div>
              ));
            }

            if (eventName === 'load' && plazaEventType === EVENT_TYPE_MEASUREMENT_POINT_UPDATE) {
              return mapLoadByPhases(values[eventName], processedEventName);
            }

            return valuesBlock(processedEventName, value, i);
          })}
      </div>
    </div>
  );
}

export default React.memo(LogView);
